import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import postData from './postData';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community'
import { purpleairStatus, purpleairColor, purpleairDescription } from './purpleairfunctions';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, Box, Typography } from '@mui/material';
import './purpleairWebpage.css';
import PageSensitiveGroup from './pageSensitiveGroup';

const PurpleairWebpageIncidentSummary = ({updateMenu}) => {

  const navigate = useNavigate();

  //arrays to hold sensor readings
  const [aryIncident, setIncident] = useState(undefined);
  const [retryCount, setRetryCount] = useState(0);

  //warning dialog variables
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');


  //  grid variables
  const gridRef = useRef();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);


  const [openSensitiveDialog, setOpenSensitiveDialog] = useState(false);

  function statusColor(params) {
    let bgcolor = purpleairColor(params.data.max25);
    return { backgroundColor: bgcolor }
  }

  function statusDescription(params) {
    return purpleairDescription(params.data.max25);
  }

  function diffMinutes(starting, ending) {
    let dateStart = new Date(starting);
    let dateEnd = new Date(ending);
    const diffInMs = dateEnd - dateStart; // difference in milliseconds
    let numMin = Math.round(diffInMs / (1000 * 60)); // convert ms to minute
    return numMin < 0 ? 0 : numMin;
  }

  function formatMinutes(params) {
    let numMin = params.data.duration;
    if (numMin > 60) {
      let numHour = Math.trunc(numMin / 60);
      let remainMin = numMin - numHour * 60;
      let strResult = `${numHour} hour`;
      if (numHour > 1) { strResult = strResult + 's' };
      if (remainMin > 0) {
        strResult = strResult + ` and ${remainMin} minutes`;
      }
      return strResult
    } else {

      return `${numMin} minutes`
    } //else > 60;
  }

  function formatIncidents(params) {
    return Math.floor(params.data.count).toLocaleString();
  }

  function formatPercentage(params) {
    let thepct = Math.round(params.data.pct * 100);
    if (thepct < 1) {
      return 'less than 1%'
    } else {
      return Math.round(params.data.pct * 100).toLocaleString() + '%';
    }
  }

  //------------------------------------------------------------
  const columnDefs = [
    { headerName: 'Spike Risk Level', field: 'risk', cellStyle: statusColor }
    , { headerName: 'Number of Incidents', field: 'count', valueFormatter: formatIncidents }
    , { headerName: 'Total time', valueGetter: formatMinutes }
    , { headerName: '% of time', field: 'pct', valueFormatter: formatPercentage }
    , { headerName: 'EPA Warning', valueGetter: statusDescription, suppressSizeToFit: true, minWidth: 350 }
  ];

  const defaultColDef = {
    sortable: true,
    autoHeight: true,
    resizable: true,
    suppressMovable: true
  };

  const gridTheme = themeQuartz.withParams({
    fontSize: 14
  })


  const popupParent = useMemo(() => {
    return document.body;
  }, []);

  const onBtnExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv();
  }, []);


  const autoSizeStrategy = useMemo(() => ({
    type: 'fitCellContents'
  }), []);

  //-------------------------------------------------------------
  // ----- handle user warning
  useEffect(() => {
    if (errorMessage !== '') {
      setOpenWarningDialog(true);
    }
  }, [errorMessage]
  )
  const handleErrorDialogOK = () => {
    setOpenWarningDialog(false);
    setErrorMessage('');
  };

  //-------------------------------------------------------------  
  // retrieve relevant submissions ------------------------------
  const getIncidents = async () => {
    var moderateCount = 0;
    var moderateDuration = 0;
    var moderateLevel = 0;
    var sensitiveCount = 0;
    var sensitiveDuration = 0;
    var sensitiveLevel = 0;
    var unhealthyCount = 0;
    var unhealthyDuration = 0;
    var unhealthyLevel = 0;
    var veryCount = 0;
    var veryDuration = 0;
    var veryLevel = 0;
    var hazardousCount = 0;
    var hazardousDuration = 0;
    var hazardousLevel = 0;

    const formData = new FormData();
    var objEntity = await postData('https://sarasotapublic.com/usrecycling/purpleairWebpageIncident.php', formData);
    setErrorMessage(objEntity.theError);
    if (objEntity.theData !== null) {

      //-------------
      //calculate the totals
      for (let i = 0; i < objEntity.theData.length; i++) {
        let theLevel = purpleairStatus(objEntity.theData[i].max25)
        switch (theLevel) {
          case 'moderate':
            moderateCount = moderateCount + 1;
            moderateDuration = moderateDuration +
              diffMinutes(objEntity.theData[i].datetimestart, objEntity.theData[i].datetimeend);
            moderateLevel = objEntity.theData[i].max25;
            break;

          case 'Unhealthy for Sensitive Groups':
            sensitiveCount = sensitiveCount + 1;
            sensitiveDuration = sensitiveDuration +
              diffMinutes(objEntity.theData[i].datetimestart, objEntity.theData[i].datetimeend);
            sensitiveLevel = objEntity.theData[i].max25;
            break

          case 'Unhealthy':
            unhealthyCount = unhealthyCount + 1;
            unhealthyDuration = unhealthyDuration +
              diffMinutes(objEntity.theData[i].datetimestart, objEntity.theData[i].datetimeend);
            unhealthyLevel = objEntity.theData[i].max25;
            break

          case 'Very unhealthy':
            veryCount = veryCount + 1;
            veryDuration = veryDuration +
              diffMinutes(objEntity.theData[i].datetimestart, objEntity.theData[i].datetimeend);
            veryLevel = objEntity.theData[i].max25;
            break

          case 'Hazardous':
            hazardousCount = hazardousCount + 1;
            hazardousDuration = hazardousDuration +
              diffMinutes(objEntity.theData[i].datetimestart, objEntity.theData[i].datetimeend);
            hazardousLevel = objEntity.theData[i].max25;
            break

          default:
            // do nothing
            alert(`the status ${theLevel} is not supported`);
            break;
        } //switch            
      } //for

      //-------------
      // calculate total time
      const startTime = "2025-05-22 08:00:00";
      const endTime =  new Date(); 
      // number of minutes between 2025-07-29 07:19:56 and 2025-08-01 16:25:33 =  545 minutes
      const totalMinutes = diffMinutes(startTime, endTime) - 545;
      //-------------
      //build the data array
      let arySummary = [];
      if (moderateCount > 0) {
        arySummary.push({ risk: 'moderate', max25: moderateLevel, count: moderateCount, duration: moderateDuration, pct: moderateDuration / totalMinutes })
      }
      if (sensitiveCount > 0) {
        arySummary.push({ risk: 'Unhealthy for Sensitive Groups', max25: sensitiveLevel, count: sensitiveCount, duration: sensitiveDuration, pct: sensitiveDuration / totalMinutes })
      }
      if (unhealthyCount > 0) {
        arySummary.push({ risk: 'Unhealthy', max25: unhealthyLevel, count: unhealthyCount, duration: unhealthyDuration, pct: unhealthyDuration / totalMinutes })
      }
      if (veryCount > 0) {
        arySummary.push({ risk: 'Very unhealthy', max25: veryLevel, count: veryCount, duration: veryDuration, pct: veryDuration / totalMinutes })
      }
      if (hazardousCount > 0) {
        arySummary.push({ risk: 'Hazardous', max25: hazardousLevel, count: hazardousCount, duration: hazardousDuration, pct: hazardousDuration / totalMinutes })
      }

      setIncident(arySummary);
      // } else {
      if (retryCount < 3) {
        // Retry after 3 seconds
        setTimeout(() => setRetryCount(retryCount + 1), 3000);
      } //retry
    } //else 
  }

  useEffect(() => {
    getIncidents();
    // eslint-disable-next-line
  }, [retryCount]);

  //----------------------------------------------------------------------
  const handleClickShowSensitive = (e) => {
    setOpenSensitiveDialog(true);
  };
  const handleSensitiveDialogOK = () => {
    setOpenSensitiveDialog(false);
  };
  const handleCellClick = (params) => {
    //go to history of all hazards if user clicks on grid
    updateMenu('Hazardous History');
    navigate("/hazardhistory");
  };
  //-------------------------------------------------------------
  return (
    <><div style={{ textAlign: 'left' }}>
      <Box display="flex" alignItems="center" gap={10}>
        <Typography variant="h5" gutterBottom>
          Summary of hazardous readings
        </Typography>
        <Button variant='contained' onClick={onBtnExport} >Export</Button>
      </Box>
      <div id="gridContainer" style={{ height: '30vh', width: '100%', textAlign: 'left' }}>
        <div style={gridStyle} >
          <AgGridReact
            ref={gridRef}
            rowData={aryIncident}
            suppressExcelExport={true}
            popupParent={popupParent}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            autoSizeStrategy={autoSizeStrategy}
            onCellClicked={handleCellClick}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>Incident reporting began on May 22, 2025  
              -- (Monitoring was suspended for maintenance between July 29 and August 1)</span>
        <button onClick={handleClickShowSensitive}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            color: 'blue',
            textDecoration: 'underline',
            cursor: 'pointer',
            font: 'inherit',
          }} ><span style={{ fontSize: '0.8em' }}>who are considered at risk</span></button>
      </div>
    </div>
      <Dialog
        open={openWarningDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Incident Summary Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogOK}>Ok</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openSensitiveDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        scroll="paper"
      >
        <DialogTitle id="alert-dialog-title">
          Who are considered sensitive to airborne concrete dust
        </DialogTitle>
        <DialogContent>
          <div style={{ maxHeight: '40vh' }}>
            <PageSensitiveGroup /></div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSensitiveDialogOK}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PurpleairWebpageIncidentSummary;